/** Typed calls for SynAssistant. Shapes mirror apps/api/src/assistant-routes.ts. */

import { apiCall, orgPath } from '../api.ts';

export interface DisplayItem {
  role: 'user' | 'assistant';
  text: string;
  tools: string[];
  sources: { title: string; url: string }[];
}

export interface PendingAction {
  tool: string;
  label: string;
  kind: string;
  input: Record<string, unknown>;
}

export interface ConversationSummary {
  id: string;
  title: string;
  client_id: string | null;
  source: 'CHAT' | 'SCHEDULE';
  updated_at: string;
}

export interface ConversationView {
  conversation: ConversationSummary;
  messages: DisplayItem[];
  pending: PendingAction[] | null;
}

export interface Skill {
  id: string;
  name: string;
  description: string;
  prompt: string;
  scope: 'CLIENT' | 'AGENCY' | 'ANY';
  built_in: boolean;
  visibility?: 'ONLY_ME' | 'EVERYONE';
  is_mine?: boolean;
}

export interface AssistantSchedule {
  id: string;
  business_unit_id: string | null;
  name: string;
  prompt: string;
  frequency: 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  run_hour: number;
  run_on: string | null;
  timezone: string;
  next_run_at: string | null;
  last_run_at: string | null;
  active: boolean;
}

export interface ToolPermission {
  tool: string;
  description: string;
  kind: 'read' | 'write';
  mode: 'ALLOW' | 'ASK' | 'BLOCK';
}

const path = (org: string, rest: string) => orgPath(org, `/assistant${rest}`);
const json = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) });

export const assistantApi = {
  /** The AI Summary widget's text for one page (the server reruns its queries). */
  summary: (org: string, body: Record<string, unknown>) =>
    apiCall<{ text: string }>(path(org, '/summary'), json('POST', body)),
  conversations: (org: string) =>
    apiCall<{ conversations: ConversationSummary[] }>(path(org, '/conversations')).then(
      (data) => data.conversations,
    ),
  conversation: (org: string, id: string) =>
    apiCall<ConversationView>(path(org, `/conversations/${id}`)),
  remove: (org: string, id: string) =>
    apiCall<{ deleted: boolean }>(path(org, `/conversations/${id}`), { method: 'DELETE' }),
  turn: (
    org: string,
    input: {
      conversation_id?: string | null;
      client_id?: string | null;
      text?: string;
      skill_id?: string | null;
      decision?: 'ALLOW' | 'DENY';
    },
  ) => apiCall<ConversationView>(path(org, '/turn'), json('POST', input)),
  skills: (org: string) =>
    apiCall<{ skills: Skill[] }>(path(org, '/skills')).then((data) => data.skills),
  saveSkill: (
    org: string,
    skill: {
      name: string;
      description: string;
      prompt: string;
      visibility: 'ONLY_ME' | 'EVERYONE';
    },
    id?: string,
  ) =>
    apiCall<{ skills: Skill[] }>(
      path(org, id ? `/skills/${id}` : '/skills'),
      json(id ? 'PUT' : 'POST', skill),
    ).then((data) => data.skills),
  removeSkill: (org: string, id: string) =>
    apiCall<{ skills: Skill[] }>(path(org, `/skills/${id}`), { method: 'DELETE' }).then(
      (data) => data.skills,
    ),
  schedules: (org: string) =>
    apiCall<{ schedules: AssistantSchedule[] }>(path(org, '/schedules')).then(
      (data) => data.schedules,
    ),
  addSchedule: (
    org: string,
    input: {
      name: string;
      prompt: string;
      frequency: AssistantSchedule['frequency'];
      run_hour: number;
      run_on: string | null;
      client_id: string | null;
    },
  ) =>
    apiCall<{ schedules: AssistantSchedule[] }>(path(org, '/schedules'), json('POST', input)).then(
      (data) => data.schedules,
    ),
  removeSchedule: (org: string, id: string) =>
    apiCall<{ schedules: AssistantSchedule[] }>(path(org, `/schedules/${id}`), {
      method: 'DELETE',
    }).then((data) => data.schedules),
  tools: (org: string) =>
    apiCall<{ tools: ToolPermission[] }>(path(org, '/tools')).then((data) => data.tools),
  setTool: (org: string, tool: string, mode: ToolPermission['mode']) =>
    apiCall<{ tools: ToolPermission[] }>(path(org, `/tools/${tool}`), json('PUT', { mode })).then(
      (data) => data.tools,
    ),
};
